import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, Signal, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';

import { filter, map, of, Subscription, switchMap } from 'rxjs';


import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgSelectModule } from '@ng-select/ng-select';
import { UserRoles } from '../../../core/models/user.model';
import { LoginService } from '../../../core/services/login.service';
import { SessionStorageService } from '../../../core/services/session-storage.service';
import { CATEGORY_ICONS } from '../../standards/icons';
import { getUserNav } from './sidebar';
import { AssignDiagnosticService } from 'src/app/core/services/assign-diagnostic.service';

@Component({
  standalone: true,
  selector: 'wf-sidebar',
  imports: [
    NgSelectModule,
    FormsModule,
    FontAwesomeModule,
    RouterLink,
    RouterLinkActive,
    NgTemplateOutlet
],
  styleUrl: 'sidebar.component.css',
  template: `
    <ng-template #linkTemplate let-link="link">
      <a
        [id]="link.id"
        class="sidebar-item"
        [routerLink]="link.route"
        routerLinkActive="sidebar-item-active"
        [routerLinkActiveOptions]="{ exact: link.exactRoute }"
      >
        {{ link.name }}
      </a>
    </ng-template>

    @let mu = managementUser();
    @if (mu && (mu.isTeacherSchoolAdmin() || mu.isTeacherSchoolReportsUser())) {
      <div class="accordion-item role-selector-wrapper">
        <label class="role-selector">
          Role:
        </label>
        <ng-select
          id="roleSelector"
          [(ngModel)]="mu.effectiveUserRole"
          (change)="updateEffectiveUserRole()"
          [clearable]="false"
        >
          <ng-option value="ROLE_TEACHER">Teacher</ng-option>
          @if (mu.isTeacherSchoolReportsUser()) {
            <ng-option value="ROLE_SCHOOL_REPORTS">School Reports</ng-option>
          }
          @else if (mu.isTeacherSchoolAdmin()) {
          <ng-option value="ROLE_SCHOOL_ADMIN">School Admin</ng-option>
          }
        </ng-select>
      </div>
    }
    <nav id="sidebar-wrapper sidebar-accordion" class="sidebar-wrapper accordion" aria-label="Management Navigation">
      @for (section of sections(); track section.id) {
        <ul
          class="sidebar-nav sidebar-nav--{{ section.color }} accordion"
          id="{{ section.id }}"
          [attr.aria-label]="section.label"
        >
          @for (category of section.categories; track category.id) {
            <li class="accordion-item">
              <div class="accordion-button-container">
                <div class="accordion-left-border"></div>
                <button
                  class="accordion-button"
                  type="button"
                  [attr.aria-controls]="category.id + '-content'"
                  [attr.aria-expanded]="currentCategory() === category.id"
                  [class]="currentCategory() !== category.id ? 'collapsed' : ''"
                  (click)="categoryClicked(category.id)"
                >
                  <fa-icon [icon]="CATEGORY_ICONS[category.id]" [fixedWidth]="true" />
                  {{ category.name }}
                </button>
              </div>
              @if (currentCategory() === category.id) {
                <div
                  id="{{ category.id }}-content"
                  class="sidebar-category"
                >
                  @if (category.links && category.links.length > 0) {
                    <ul>
                      @for (link of category.links; track link.route) {
                        <li>
                          <ng-container [ngTemplateOutlet]="linkTemplate" [ngTemplateOutletContext]="{ link }" />
                        </li>
                      }
                    </ul>
                  }
                  @for (group of category.groups; track group.name) {
                    <div class="sidebar-group-label">
                      <span>{{ group.name }}</span>
                    </div>
                    <ul>
                      @for (link of group.links; track link.route) {
                        <li>
                          <ng-container [ngTemplateOutlet]="linkTemplate" [ngTemplateOutletContext]="{ link }" />
                        </li>
                      }
                    </ul>
                  }
                </div>
              }
            </li>
          }
        </ul>
      }
    </nav>
  `
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly loginService = inject(LoginService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly assignDiagnosticService = inject(AssignDiagnosticService);
  private readonly router = inject(Router);

  readonly showManageTeacherSubscriptions = toSignal(
    toObservable(this.sessionStorageService.userData).pipe(
      switchMap(u => {
        if (!u) {
          return of(false);
        }
        if (u.userRole === UserRoles.CUSTOMER_SERVICE) {
          return of(true);
        }
        if (u.userRole !== UserRoles.SCHOOL_ADMIN && u.effectiveUserRole !== UserRoles.SCHOOL_ADMIN) {
          return of(false);
        }
        return this.assignDiagnosticService
          .getSchoolSubscriptionSummary(u.schoolID)
          .pipe(map(sw => {
            const showDiagnostic = !sw.diagnostic.unlimited && (
              sw.diagnostic.unassigned > 0
              || sw.diagnostic.unconsumed > 0
              || sw.diagnostic.teacher > 0
            );
            const showSystem = !sw.system.unlimited && (
              sw.system.unassigned > 0
              || sw.system.unconsumed > 0
              || sw.system.teacher > 0
            );
            return showDiagnostic || showSystem;
          }));
      })
    ),
    { initialValue: false }
  );

  readonly sections = computed(() => {
    const userData = this.managementUser();
    if (!this.isUserLoggedInInternal() || !userData) {
      return undefined;
    }
    return getUserNav(
      userData,
      userData.schoolHasExternalIdentityProvider || this.sessionStorageService.getSSOInfo() !== null,
      this.demoModeEnabled(),
      this.showManageTeacherSubscriptions()
    );
  })

  readonly CATEGORY_ICONS = CATEGORY_ICONS;

  loginStatusSubscription: Subscription | undefined ;
  routeChangeSubscription: Subscription | undefined ;

  readonly managementUser = this.sessionStorageService.userData;
  readonly currentCategory = signal('');
  readonly shouldShow = signal(true);
  readonly isUserLoggedInInternal = signal(false);
  readonly isUserLoggedIn = signal(false);

  demoModeEnabled: Signal<boolean> = inject(SessionStorageService).isDemoMode ;
  destroyRef: DestroyRef = inject(DestroyRef) ;

  ngOnInit(): void {
    this.loginStatusSubscription = this.loginService.loginStatus$.subscribe((loggedIn) => {
      this.isUserLoggedInInternal.set(loggedIn);
    }) ;

    // NOTE: This is a bit of a hack, because we are listening to the login event, which is emitted
    //     : immediately on the login, it causes a weird bug if we immediately reflect that to our `isUserLoggedIn`
    //     : variable (which causes the sidebar to show up), and on login this is a problem because our route
    //     : might not load immediately (so the sidebar appears before the login box is removed). So instead,
    //     : we handle the emitted login value to an internal cached value and set it when our route change ends
    this.routeChangeSubscription = this.router.events.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter(e => e instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isUserLoggedIn.set(this.isUserLoggedInInternal());

      // When we are on the diagnostic details page, we should not show the sidebar
      this.shouldShow.set(!event.url.includes('detail'));

      // Attempt to determine our sidebar category to expand
      let deepRoute = this.router.routerState.root ;
      while (deepRoute.firstChild)
      {
        deepRoute = deepRoute.firstChild ;
      }
      this.currentCategory.set(deepRoute.snapshot.data['category'] ?? '');
    }) ;
  }

  ngOnDestroy() {
    this.sessionStorageService.setIsDemoModeEnabled(false);
    this.loginStatusSubscription?.unsubscribe() ;
    this.routeChangeSubscription?.unsubscribe() ;
  }

  categoryClicked(category: string) {
    this.currentCategory.update(c => c === category ? '' : category);
  }

  updateEffectiveUserRole()
  {
    const managementUser = this.managementUser();
    if (managementUser)
    {
      // Save our management user back to state for user in other parts of the app
      // Needs to set before refreshingNav
      this.sessionStorageService.setUserData(managementUser) ;

      if (managementUser.effectiveUserRole === UserRoles.SCHOOL_REPORTS || managementUser.effectiveUserRole == UserRoles.SCHOOL_ADMIN)
      {
        this.router.navigateByUrl('/reports/school/dashboard') ;
      }
      else if (managementUser.effectiveUserRole === UserRoles.TEACHER)
      {
        // If going to teacher mode, set scope to themselves
        this.sessionStorageService.setSelectedTeacher(managementUser);

        this.router.navigateByUrl('/dashboard/system') ;
      }
    }
  }
}
